import { languageOf, type LanguageCode } from './languages'

/**
 * How hard the content should be.
 *
 * `hskLevel` was decoration for most of this app's life: every island carried
 * one, two screens printed it as a badge, and nothing anywhere read it. That
 * was fine until you looked at what a new Mandarin learner actually meets on
 * their first day — the five identity islands, which are the first thing in the
 * list and simultaneously the hardest content in the set. 一开始只是因为工作需
 * 要，后来就真的喜欢上了。is twenty characters, uses 一开始…后来就, and is the
 * second block of the second island. The other four languages never had this
 * problem: their starter islands are all level 1–2.
 *
 * So the number now does three things — orders the island list, picks what a
 * new install is seeded with, and tells the generator what to aim at — and none
 * of them is a gate. Everything stays openable. Someone who wants to stretch
 * should be allowed to; the point is only that the stretch is their choice
 * rather than the first thing that happens to them.
 */

export const MIN_LEVEL = 1
export const MAX_LEVEL = 6

/**
 * The learner's level in one language.
 *
 * Always read the level through this rather than off `settings.hskLevel`. The
 * per-language map is the real answer; `hskLevel` is the account-wide fallback
 * for a language they have not been asked about yet.
 */
export function levelFor(
  settings: { hskLevel?: number; levelByLanguage?: Record<string, number> },
  language: string,
): number {
  return clampLevel(settings.levelByLanguage?.[language] ?? settings.hskLevel)
}

/** Anything outside 1–6, including nonsense, lands on the middle. */
export function clampLevel(level: unknown): number {
  const n = Number(level)
  if (!Number.isFinite(n)) return 3
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(n)))
}

/**
 * Three tiers, not six.
 *
 * HSK is a real scale a Mandarin learner may well know their own place on, but
 * the other four languages have no such thing, and six radio buttons at signup
 * is a quiz before you have seen the product. Three named tiers is a question
 * anybody can answer in a second, and the stored number is still 1–6 so a
 * generated island can be more precise than the chooser.
 */
export interface LevelTier {
  level: number
  label: string
  /** The scale name, for people who know where they are on it. */
  scale: { zh: string; other: string }
  blurb: string
}

export const LEVEL_TIERS: LevelTier[] = [
  {
    level: 2,
    label: 'Just starting',
    scale: { zh: 'HSK 1–2', other: 'A1–A2' },
    blurb: 'Short lines you can deliver whole, without assembling them first.',
  },
  {
    level: 3,
    label: 'Getting going',
    scale: { zh: 'HSK 3–4', other: 'B1' },
    blurb: 'You can hold a simple conversation and want longer, more natural blocks.',
  },
  {
    level: 5,
    label: 'Comfortable',
    scale: { zh: 'HSK 5+', other: 'B2–C1' },
    blurb: 'You are after register and idiom, not grammar.',
  },
]

/** The tier a stored level belongs to — the highest one it reaches. */
export function tierFor(level: number): LevelTier {
  const n = clampLevel(level)
  let found = LEVEL_TIERS[0]
  for (const tier of LEVEL_TIERS) if (n >= tier.level) found = tier
  return found
}

/**
 * What one step on the scale is called.
 *
 * Mandarin learners know their HSK number, so the button says the number. For
 * the other four there is no HSK, and "Level 4" means nothing while "B2" is
 * the label on every course and exam they have met — so the CEFR band that
 * levelGuidance() already pitches the generator at is what the button shows.
 */
export function stepLabel(language: LanguageCode, level: number): string {
  const n = clampLevel(level)
  if (language === 'zh') return String(n)
  return ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'][n - 1]
}

/**
 * How to name one point on the scale in running text.
 *
 * "HSK 2" for Mandarin, but plain "A2" for the rest — the CEFR band already
 * says what scale it is, so `levelName` + `stepLabel` produced "Level A2",
 * which reads as a stutter. Production caught this: "Above Level A2".
 */
export function scaleStep(language: LanguageCode, level: number): string {
  const step = stepLabel(language, level)
  return language === 'zh' ? `${levelName(language)} ${step}` : step
}

/** "HSK" for Mandarin, "Level" for everything else. Prefer scaleStep in prose. */
export function levelName(language: LanguageCode): string {
  return language === 'zh' ? 'HSK' : 'Level'
}

export function scaleFor(language: LanguageCode, tier: LevelTier): string {
  return language === 'zh' ? tier.scale.zh : tier.scale.other
}

/**
 * How hard a scenario is: the hardest island it makes you reach for.
 *
 * Scenarios carry no level of their own and should not — one would be a second
 * number to keep true, and it would be a guess. A scenario is answerable
 * exactly when you have the islands its beats point at, so the hardest of
 * those is the honest answer, and it stays correct when an island is re-levelled
 * or the learner rewrites one.
 *
 * A beat pointing at an island that is not in the library any more counts for
 * nothing here — the view has its own, louder warning for that.
 */
export function scenarioLevel(
  scenario: { beats: { expectIslandId: string }[] },
  islands: { id: string; hskLevel: number }[],
): number {
  const byId = new Map(islands.map((i) => [i.id, i]))
  const levels = scenario.beats
    .map((b) => byId.get(b.expectIslandId)?.hskLevel)
    .filter((n): n is number => typeof n === 'number')
  return levels.length ? clampLevel(Math.max(...levels)) : MIN_LEVEL
}

/**
 * Split a list into what the learner can drill now and what is still ahead.
 *
 * Order *within* each group is left exactly as it was found. Sorting by level
 * would shuffle the library of every existing user on the day this ships, for
 * no gain — the only thing anyone needs is for the reachable islands not to be
 * buried under the ones that are not.
 */
/**
 * A beginner island, and the one that replaces it once you have outgrown it.
 *
 * The HSK 1-2 identity islands were written as gentler versions of the five
 * that were already there — same five topics, the ones you are asked in the
 * first two minutes of any conversation. That is right for a beginner and
 * clutter for everybody else: at HSK 6 the list showed "My work (starting
 * out)" directly above "My work", five times over, and a learner counting
 * their islands found ten where there were five topics.
 *
 * Mandarin only. The Latin languages' A1 additions are their own topics —
 * introducing yourself, ordering at a counter — not gentler retellings, so
 * there is nothing there to outgrow.
 */
const SUPERSEDED_BY: Record<string, string> = {
  isl_work_a1: 'isl_work',
  isl_why_a1: 'isl_why_chinese',
  isl_family_a1: 'isl_family',
  isl_where_a1: 'isl_home',
  isl_free_a1: 'isl_hobbies',
}

export function splitByLevel<T extends { id?: string; hskLevel: number; isStarter?: boolean }>(
  items: T[],
  level: number,
): { ready: T[]; ahead: T[]; outgrown: T[] } {
  const n = clampLevel(level)
  const byId = new Map(items.map((i) => [i.id, i]))

  /*
   * Outgrown, not gone. Nothing is deleted and nothing is unreachable — the
   * replacement has to actually be in the library before its beginner version
   * steps aside, and dropping the level in the rail brings it straight back.
   * That is the same bargain the "above your level" line already offers, run
   * in the other direction.
   */
  const isOutgrown = (item: T) => {
    if (item.isStarter === false || !item.id) return false
    const replacementId = SUPERSEDED_BY[item.id]
    if (!replacementId) return false
    const replacement = byId.get(replacementId)
    return Boolean(replacement) && (replacement as T).hskLevel <= n
  }
  /*
   * Only content WE shipped can be above you.
   *
   * The mis-pitch this whole feature exists to fix was ours: our starter set,
   * our HSK 3 identity islands at the top of a beginner's first screen. An
   * island the learner wrote, or asked Claude for, is one they chose — and the
   * app has no business telling someone their own five lines about their own
   * job are too hard for them, still less quietly dropping them out of Today,
   * out of the rail count and out of every ambush. Hand-built drafts are born
   * at HSK 3 and generated ones at whatever the model answered, so without
   * this every beginner's own work would vanish from their practice the moment
   * they saved it.
   */
  const outgrown = items.filter(isOutgrown)
  const ahead = items.filter(
    (i) => !outgrown.includes(i) && i.isStarter !== false && (i.hskLevel ?? 1) > n,
  )
  const moved = new Set([...outgrown, ...ahead])
  return { ready: items.filter((i) => !moved.has(i)), ahead, outgrown }
}

/**
 * What to tell the model.
 *
 * Concrete and mechanical on purpose. "Write it for a beginner" produces the
 * same island as no instruction at all; "four to eight characters, no 把, no
 * resultative complements" produces a different one.
 */
export function levelGuidance(language: LanguageCode, level: number): string {
  const n = clampLevel(level)

  if (language === 'zh') {
    const zh: Record<number, string> = {
      1: 'HSK 1. The first few hundred words only. Blocks of four to eight characters. Simple 主谓宾 order, 是/有/在, 很+adjective, 喜欢, the 的 possessive. No 把, no 被, no resultative or directional complements, no 只有…才, no 一…就, no 除了…以外.',
      2: 'HSK 1–2. Blocks of five to ten characters. You may use 了, 过, 会/能/可以, 因为…所以, 但是, 还可以, 有点儿, and simple time and place phrases. Still no 把, no 被, no complex complements, no four-character idioms.',
      3: 'HSK 3. Blocks of eight to fourteen characters. Resultative complements, 把 where it is natural, 一边…一边, 虽然…但是, 越来越 are all fair. Keep the vocabulary everyday.',
      4: 'HSK 4. Blocks of ten to eighteen characters. Fuller sentences, opinions with reasons, common 成语 where a speaker would really use one.',
      5: 'HSK 5. Blocks of twelve to twenty-two characters. Register matters more than grammar now — write what an educated native speaker says, including the hedges and the discourse particles.',
      6: 'HSK 6. No length constraint. Idiom, nuance and register throughout; assume the learner understands the grammar and is here for how a native speaker would actually put it.',
    }
    return zh[n]
  }

  const lang = languageOf(language)
  const cefr: Record<number, string> = {
    1: `CEFR A1. Short sentences a learner can deliver whole — six to ten words. Present tense, the most common verbs, no subjunctive, no compound tenses, no idiom.`,
    2: `CEFR A2. Eight to twelve words. Past and future are fine, as are the everyday modal verbs. Avoid the subjunctive and anything literary.`,
    3: `CEFR B1. Twelve to eighteen words. Opinions with reasons, subordinate clauses, the tenses an ordinary conversation actually uses.`,
    4: `CEFR B2. Fuller sentences with real connective tissue. The subjunctive where ${lang.name} genuinely requires it, and common idiom.`,
    5: `CEFR C1. Register and idiom over grammar — write what an educated native speaker says, hedges and discourse markers included.`,
    6: `CEFR C2. No constraint. Assume the grammar is known; the value here is nuance and register.`,
  }
  return cefr[n]
}
